import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import InfoIcon from '@mui/icons-material/Info';
import { Typography, Box, Divider } from '@mui/material';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useGetReservationPricePreviewMutation } from '../Service/extendedReservationsApi';
import { ReservationPricePreviewRequest } from '../Types/reservationTypes';
import {
  DISCOUNT_AMOUNT_MIN_THRESHOLD,
  MONEY_AMOUNT_MIN_THRESHOLD,
} from '../Constants/reservationConstants';

interface Props {
  bookingId: number | null;
  selectedBoardingPlaceId: number | null;
  numberOfParticipants: number | null;
  selectedAdditionalOptionMappingIds?: number[];
  localUnitPrice?: number;
  localBoardingPlacePrice?: number;
  discountAmount?: number | null;
  totalPriceOverride?: number | null;
}

const ReservationPricePreview: React.FC<Props> = ({
  bookingId,
  selectedBoardingPlaceId,
  numberOfParticipants,
  selectedAdditionalOptionMappingIds,
  localUnitPrice,
  localBoardingPlacePrice,
  discountAmount,
  totalPriceOverride,
}) => {
  const [getPreview, { data, isLoading, error }] = useGetReservationPricePreviewMutation();
  const [lastPayload, setLastPayload] = useState<ReservationPricePreviewRequest | null>(null);
  const [lastPayloadIdsKey, setLastPayloadIdsKey] = useState('');
  const fetchPreview = useMemo(
    () =>
      debounce((payload: ReservationPricePreviewRequest, idsKey: string) => {
        getPreview(payload);
        setLastPayload(payload);
        setLastPayloadIdsKey(idsKey);
      }, 400),
    [getPreview]
  );

  useEffect(() => {
    return () => {
      fetchPreview.cancel();
    };
  }, [fetchPreview]);

  const stableSelectedAdditionalOptionMappingIds = useMemo(
    () => [...(selectedAdditionalOptionMappingIds ?? [])].sort((a, b) => a - b),
    [selectedAdditionalOptionMappingIds]
  );

  const isInvalidOverride =
    Number.isFinite(totalPriceOverride) &&
    totalPriceOverride! > 0 &&
    totalPriceOverride! < MONEY_AMOUNT_MIN_THRESHOLD;
  const normalizedTotalPriceOverride =
    Number.isFinite(totalPriceOverride) && (totalPriceOverride as number) >= MONEY_AMOUNT_MIN_THRESHOLD
      ? (totalPriceOverride as number)
      : null;
  const normalizedDiscountAmount =
    Number.isFinite(discountAmount) && (discountAmount as number) >= DISCOUNT_AMOUNT_MIN_THRESHOLD
      ? (discountAmount as number)
      : null;
  const hasTotalPriceOverride = normalizedTotalPriceOverride !== null;
  const shouldSkipPreviewRequest = hasTotalPriceOverride || isInvalidOverride;

  useEffect(() => {
    if (shouldSkipPreviewRequest) {
      fetchPreview.cancel();
      return;
    }

    if (bookingId && selectedBoardingPlaceId && numberOfParticipants && numberOfParticipants > 0) {
      const currentIdsKey = stableSelectedAdditionalOptionMappingIds.join(',');
      const payload: ReservationPricePreviewRequest = {
        bookingId,
        selectedBoardingPlaceId,
        numberOfParticipants,
        selectedAdditionalOptionMappingIds:
          stableSelectedAdditionalOptionMappingIds.length > 0
            ? stableSelectedAdditionalOptionMappingIds
            : undefined,
      };
      if (
        !lastPayload ||
        lastPayload.bookingId !== payload.bookingId ||
        lastPayload.selectedBoardingPlaceId !== payload.selectedBoardingPlaceId ||
        lastPayload.numberOfParticipants !== payload.numberOfParticipants ||
        lastPayloadIdsKey !== currentIdsKey
      ) {
        fetchPreview(payload, currentIdsKey);
      }
    }
  }, [
    bookingId,
    selectedBoardingPlaceId,
    numberOfParticipants,
    lastPayload,
    lastPayloadIdsKey,
    fetchPreview,
    stableSelectedAdditionalOptionMappingIds,
    shouldSkipPreviewRequest,
  ]);
  const formatPrice = (value: number | null | undefined): string | null => {
    if (value === null || value === undefined) return null;
    return `${value.toFixed(2)} zł`;
  };

  const renderRow = (label: string, formattedValue: string | null) =>
    formattedValue ? (
      <Typography variant="body2">
        {label}: <strong>{formattedValue}</strong>
      </Typography>
    ) : null;

  const shouldShowDiscount =
    normalizedDiscountAmount !== null && !hasTotalPriceOverride && !isInvalidOverride;
  const discountedTotalPrice =
    data && normalizedDiscountAmount !== null
      ? Math.max(0, data.totalPrice - normalizedDiscountAmount)
      : null;

  return (
    <CustomCard
      icon={<InfoIcon fontSize="large" color="primary" />}
      title="Podgląd ceny rezerwacji"
      sx={{
        mb: 3,
        '&::before': {
          content: 'none',
        },
        '& *::before': {
          content: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!bookingId && (
          <Typography variant="body2">
            Wybierz termin rezerwacji, aby zobaczyć cenę bazową.
          </Typography>
        )}

        {bookingId && !selectedBoardingPlaceId && (
          <>
            {renderRow(
              'Cena bazowa za osobę',
              formatPrice(localUnitPrice ?? data?.unitPrice ?? null)
            )}
            <Typography variant="body2">
              Wybierz miejsce wsiadania, aby zobaczyć pełną cenę.
            </Typography>
          </>
        )}

        {bookingId &&
          selectedBoardingPlaceId &&
          (!numberOfParticipants || numberOfParticipants <= 0) && (
            <>
              {renderRow('Cena bazowa za osobę', formatPrice(localUnitPrice ?? data?.unitPrice))}
              {renderRow(
                'Cena miejsca wsiadania za osobę',
                formatPrice(localBoardingPlacePrice ?? data?.boardingPlacePrice)
              )}
              <Typography variant="body2">
                Podaj liczbę uczestników, aby zobaczyć całą sumę.
              </Typography>
            </>
          )}

        {bookingId &&
          selectedBoardingPlaceId &&
          numberOfParticipants &&
          numberOfParticipants > 0 && (
            <>
              {!hasTotalPriceOverride && isLoading && <Typography variant="body2">Przeliczam cenę…</Typography>}
              {!hasTotalPriceOverride && !isInvalidOverride && error && (
                <Typography variant="body2" color="error">
                  Błąd pobierania ceny!
                </Typography>
              )}
              {isInvalidOverride && (
                <Typography variant="body2" color="error">
                  Kwota całkowita musi wynosić co najmniej 0,01.
                </Typography>
              )}
              {hasTotalPriceOverride && (
                <>
                  {renderRow(
                    'Cena bazowa za osobę',
                    formatPrice(localUnitPrice ?? data?.unitPrice ?? null)
                  )}
                  {renderRow(
                    'Cena miejsca wsiadania za osobę',
                    formatPrice(localBoardingPlacePrice ?? data?.boardingPlacePrice ?? null)
                  )}
                  <Divider />
                  <Typography variant="h6" color="primary">
                    Cena końcowa: <strong>{formatPrice(normalizedTotalPriceOverride)}</strong>
                  </Typography>
                  <Typography variant="body2">Cena nadpisana</Typography>
                </>
              )}
              {!hasTotalPriceOverride && !isInvalidOverride && data && (
                <>
                  {renderRow('Cena bazowa za osobę', formatPrice(data.unitPrice))}
                  {renderRow(
                    'Cena miejsca wsiadania za osobę',
                    formatPrice(data.boardingPlacePrice)
                  )}
                  {renderRow('Suma za osobę', formatPrice(data.pricePerPerson))}
                  {shouldShowDiscount && renderRow('Rabat', formatPrice(normalizedDiscountAmount))}
                  <Divider />
                  <Typography variant="h6" color="primary">
                    Cena końcowa: <strong>{formatPrice(shouldShowDiscount ? discountedTotalPrice : data.totalPrice)}</strong>
                  </Typography>
                </>
              )}
            </>
          )}
      </Box>
    </CustomCard>
  );
};

export default ReservationPricePreview;

